import React, { useState } from 'react';
import { Camera, MapPin, Link, Calendar, Edit2, Plus, Award, BookOpen, Building2, FileText, Save, X } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Avatar, AvatarImage, AvatarFallback } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import PostCard from '../components/feed/PostCard';
import SimplifyMyTrainingProfile from '../components/profile/SimplifyMyTrainingProfile';
import { useAuth } from '../contexts/AuthContext';
import { mockPosts } from '../data/mock';
import { toast } from '../hooks/use-toast';

const Profile = () => {
  const { user, updateUser } = useAuth();
  const [posts, setPosts] = useState(mockPosts.filter(post => post.author.name === user.name));
  const [showTrainerProfile, setShowTrainerProfile] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editedUser, setEditedUser] = useState(user);
  const [showAddExperience, setShowAddExperience] = useState(false);
  const [showAddEducation, setShowAddEducation] = useState(false);
  const [showAddSkill, setShowAddSkill] = useState(false);
  const [newExperience, setNewExperience] = useState({
    title: '',
    company: '',
    duration: '',
    location: '',
    description: ''
  });
  const [newEducation, setNewEducation] = useState({
    school: '',
    degree: '',
    year: ''
  });
  const [newSkill, setNewSkill] = useState('');

  const handleLikePost = (postId) => {
    setPosts(posts.map(post => {
      if (post.id === postId) {
        return {
          ...post,
          isLiked: !post.isLiked,
          likes: post.isLiked ? post.likes - 1 : post.likes + 1
        };
      }
      return post;
    }));
  };

  const handleEditProfile = () => {
    setIsEditing(true);
    setEditedUser({ ...user });
  };

  const handleSaveProfile = () => {
    updateUser(editedUser);
    setIsEditing(false);
    toast({
      title: "Profile updated!",
      description: "Your profile has been successfully updated.",
    });
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditedUser(user);
  };

  const handleAddExperience = () => {
    if (!newExperience.title || !newExperience.company) {
      toast({
        title: "Missing information",
        description: "Please fill in at least the title and company.",
        variant: "destructive",
      });
      return;
    }

    const experience = {
      id: Date.now().toString(),
      ...newExperience
    };

    const updatedUser = {
      ...user,
      experience: [...(user.experience || []), experience]
    };
    
    updateUser(updatedUser);
    setNewExperience({
      title: '',
      company: '',
      duration: '',
      location: '',
      description: ''
    });
    setShowAddExperience(false);
    
    toast({
      title: "Experience added!",
      description: "New work experience has been added to your profile.",
    });
  };

  const handleAddEducation = () => {
    if (!newEducation.school || !newEducation.degree) {
      toast({
        title: "Missing information",
        description: "Please fill in at least the school and degree.",
        variant: "destructive",
      });
      return;
    }

    const education = {
      ...newEducation
    };

    const updatedUser = {
      ...user,
      education: [...(user.education || []), education]
    };
    
    updateUser(updatedUser);
    setNewEducation({
      school: '',
      degree: '',
      year: ''
    });
    setShowAddEducation(false);
    
    toast({
      title: "Education added!",
      description: "New education has been added to your profile.",
    });
  };

  const handleAddSkill = () => {
    if (!newSkill.trim()) {
      toast({
        title: "Missing information",
        description: "Please enter a skill name.",
        variant: "destructive",
      });
      return;
    }

    const updatedUser = {
      ...user,
      skills: [...(user.skills || []), newSkill.trim()]
    };
    
    updateUser(updatedUser);
    setNewSkill('');
    setShowAddSkill(false);
    
    toast({
      title: "Skill added!",
      description: "New skill has been added to your profile.",
    });
  };

  const handleMoreActions = () => {
    toast({
      title: "More options",
      description: "Additional profile options will be available soon.",
    });
  };

  const handleViewProfileViewers = () => {
    toast({
      title: "Profile viewers",
      description: "47 people viewed your profile this week. Premium feature for detailed analytics.",
    });
  };

  const handleViewConnections = () => {
    toast({
      title: "View connections",
      description: `You have ${user?.connections?.toLocaleString()} connections.`,
    });
  };

  const handleViewFollowers = () => {
    toast({
      title: "View followers", 
      description: `You have ${user?.followers?.toLocaleString()} followers.`,
    });
  };

  return (
    <div className="space-y-6">
      {/* Profile Header */}
      <Card>
        <div className="relative">
          <div className="h-32 bg-gradient-to-r from-blue-500 to-blue-600 rounded-t-lg"></div>
          <Button
            variant="ghost"
            size="sm"
            className="absolute top-4 right-4 text-white hover:bg-white/20"
          >
            <Camera className="w-4 h-4 mr-2" />
            Edit cover
          </Button>
          <div className="absolute -bottom-16 left-6">
            <div className="relative">
              <Avatar className="w-32 h-32 border-4 border-white">
                <AvatarImage src={user?.avatar} alt={user?.name} />
                <AvatarFallback className="text-2xl">{user?.name?.charAt(0)}</AvatarFallback>
              </Avatar>
              <Button
                variant="ghost"
                size="sm"
                className="absolute bottom-2 right-2 w-8 h-8 rounded-full bg-white border shadow-sm p-0"
              >
                <Camera className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
        <CardContent className="pt-20 pb-4">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center space-x-2 mb-2">
                <h1 className="text-2xl font-bold">{user?.name}</h1>
                <Badge variant="outline" className="text-blue-600">
                  <Award className="w-3 h-3 mr-1" />
                  Premium
                </Badge>
              </div>
              <p className="text-lg text-gray-700 mb-2">{user?.title}</p>
              <div className="flex items-center space-x-4 text-sm text-gray-600 mb-4">
                <div className="flex items-center">
                  <MapPin className="w-4 h-4 mr-1" />
                  {user?.location}
                </div>
                <div className="flex items-center">
                  <Link className="w-4 h-4 mr-1" />
                  Contact info
                </div>
                <div className="flex items-center">
                  <Calendar className="w-4 h-4 mr-1" />
                  Joined March 2020
                </div>
              </div>
              <div className="flex items-center space-x-6 text-sm mb-4">
                <button 
                  onClick={handleViewConnections}
                  className="text-blue-600 font-semibold hover:underline cursor-pointer transition-colors"
                >
                  {user?.connections?.toLocaleString()} connections
                </button>
                <button 
                  onClick={handleViewFollowers}
                  className="text-blue-600 font-semibold hover:underline cursor-pointer transition-colors"
                >
                  {user?.followers?.toLocaleString()} followers
                </button>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <Button 
                size="sm" 
                variant="outline"
                onClick={() => setShowTrainerProfile(true)}
                className="bg-gradient-to-r from-green-500 to-green-600 text-white border-none hover:from-green-600 hover:to-green-700"
              >
                <FileText className="w-4 h-4 mr-2" />
                View Trainer Profile
              </Button>
              <Button size="sm" variant="outline" onClick={handleMoreActions}>
                More
              </Button>
              {!isEditing ? (
                <Button size="sm" className="bg-blue-600 hover:bg-blue-700" onClick={handleEditProfile}>
                  <Edit2 className="w-4 h-4 mr-2" />
                  Edit profile
                </Button>
              ) : (
                <div className="flex space-x-2">
                  <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={handleSaveProfile}>
                    <Save className="w-4 h-4 mr-2" />
                    Save
                  </Button>
                  <Button size="sm" variant="outline" onClick={handleCancelEdit}>
                    <X className="w-4 h-4 mr-2" />
                    Cancel
                  </Button>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* About Section */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>About</CardTitle>
            <Button variant="ghost" size="sm" onClick={handleEditProfile}>
              <Edit2 className="w-4 h-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {!isEditing ? (
            <p className="text-gray-700 leading-relaxed">{user?.about}</p>
          ) : (
            <Textarea
              value={editedUser?.about || ''}
              onChange={(e) => setEditedUser(prev => ({ ...prev, about: e.target.value }))}
              placeholder="Write about your professional background, skills, and achievements..."
              className="min-h-[100px]"
            />
          )}
        </CardContent>
      </Card>

      {/* Experience */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center">
              <Building2 className="w-5 h-5 mr-2" />
              Experience
            </CardTitle>
            <div className="flex items-center space-x-2">
              <Dialog open={showAddExperience} onOpenChange={setShowAddExperience}>
                <DialogTrigger asChild>
                  <Button variant="ghost" size="sm">
                    <Plus className="w-4 h-4 mr-1" />
                    Add position
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Add Work Experience</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div>
                      <label className="text-sm font-medium">Title</label>
                      <Input
                        placeholder="e.g. Senior Training Specialist"
                        value={newExperience.title}
                        onChange={(e) => setNewExperience(prev => ({ ...prev, title: e.target.value }))}
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium">Company</label>
                      <Input
                        placeholder="e.g. Microsoft Corporation"
                        value={newExperience.company}
                        onChange={(e) => setNewExperience(prev => ({ ...prev, company: e.target.value }))}
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium">Duration</label>
                      <Input
                        placeholder="e.g. January 2020 - Present"
                        value={newExperience.duration}
                        onChange={(e) => setNewExperience(prev => ({ ...prev, duration: e.target.value }))}
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium">Location</label>
                      <Input
                        placeholder="e.g. New York, NY"
                        value={newExperience.location}
                        onChange={(e) => setNewExperience(prev => ({ ...prev, location: e.target.value }))}
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium">Description</label>
                      <Textarea
                        placeholder="Describe your role and responsibilities..."
                        value={newExperience.description}
                        onChange={(e) => setNewExperience(prev => ({ ...prev, description: e.target.value }))}
                      />
                    </div>
                    <div className="flex justify-end space-x-2">
                      <Button variant="outline" onClick={() => setShowAddExperience(false)}>Cancel</Button>
                      <Button onClick={handleAddExperience}>Add Experience</Button>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
              <Button variant="ghost" size="sm" onClick={handleEditProfile}>
                <Edit2 className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {user?.experience?.map((exp, index) => (
            <div key={exp.id}>
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center">
                  <Building2 className="w-6 h-6 text-gray-600" />
                </div>
                <div className="flex-1">
                  <h4 className="font-semibold text-lg">{exp.title}</h4>
                  <p className="text-blue-600 font-medium">{exp.company}</p>
                  <p className="text-sm text-gray-600 mb-1">{exp.duration}</p>
                  <p className="text-sm text-gray-600 mb-2">{exp.location}</p>
                  <p className="text-sm text-gray-700">{exp.description}</p>
                </div>
              </div>
              {index < user.experience.length - 1 && <Separator className="mt-6" />}
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Education */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center">
              <BookOpen className="w-5 h-5 mr-2" />
              Education
            </CardTitle>
            <div className="flex items-center space-x-2">
              <Dialog open={showAddEducation} onOpenChange={setShowAddEducation}>
                <DialogTrigger asChild>
                  <Button variant="ghost" size="sm">
                    <Plus className="w-4 h-4 mr-1" />
                    Add education
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Add Education</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div>
                      <label className="text-sm font-medium">School</label>
                      <Input
                        placeholder="e.g. Harvard University"
                        value={newEducation.school}
                        onChange={(e) => setNewEducation(prev => ({ ...prev, school: e.target.value }))}
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium">Degree</label>
                      <Input
                        placeholder="e.g. Master of Science in Adult Learning"
                        value={newEducation.degree}
                        onChange={(e) => setNewEducation(prev => ({ ...prev, degree: e.target.value }))}
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium">Year</label>
                      <Input
                        placeholder="e.g. 2016"
                        value={newEducation.year}
                        onChange={(e) => setNewEducation(prev => ({ ...prev, year: e.target.value }))}
                      />
                    </div>
                    <div className="flex justify-end space-x-2">
                      <Button variant="outline" onClick={() => setShowAddEducation(false)}>Cancel</Button>
                      <Button onClick={handleAddEducation}>Add Education</Button>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
              <Button variant="ghost" size="sm" onClick={handleEditProfile}>
                <Edit2 className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {user?.education?.map((edu, index) => (
            <div key={index} className="flex items-start space-x-4">
              <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center">
                <BookOpen className="w-6 h-6 text-gray-600" />
              </div>
              <div>
                <h4 className="font-semibold">{edu.school}</h4>
                <p className="text-gray-600">{edu.degree}</p>
                <p className="text-sm text-gray-500">{edu.year}</p>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Skills */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Skills & Endorsements</CardTitle>
            <div className="flex items-center space-x-2">
              <Dialog open={showAddSkill} onOpenChange={setShowAddSkill}>
                <DialogTrigger asChild>
                  <Button variant="ghost" size="sm">
                    <Plus className="w-4 h-4 mr-1" />
                    Add skill
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Add Skill</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div>
                      <label className="text-sm font-medium">Skill Name</label>
                      <Input
                        placeholder="e.g. Leadership Training"
                        value={newSkill}
                        onChange={(e) => setNewSkill(e.target.value)}
                      />
                    </div>
                    <div className="flex justify-end space-x-2">
                      <Button variant="outline" onClick={() => setShowAddSkill(false)}>Cancel</Button>
                      <Button onClick={handleAddSkill}>Add Skill</Button>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
              <Button variant="ghost" size="sm" onClick={handleEditProfile}>
                <Edit2 className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {user?.skills?.slice(0, 3).map((skill, index) => (
              <div key={index} className="border rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <h4 className="font-semibold">{skill}</h4>
                  <Button variant="outline" size="sm">
                    Endorse
                  </Button>
                </div>
                <p className="text-sm text-gray-600 mt-1">
                  Endorsed by {Math.floor(Math.random() * 50) + 10} connections
                </p>
              </div>
            ))}
          </div>
          <Button variant="ghost" className="w-full mt-4 text-blue-600">
            Show all {user?.skills?.length} skills
          </Button>
        </CardContent>
      </Card>

      {/* Activity */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Activity</CardTitle>
            <Button variant="ghost" size="sm" className="text-blue-600">
              Show all posts
            </Button>
          </div>
          <p className="text-sm text-gray-600">
            {user?.followers?.toLocaleString()} followers
          </p>
        </CardHeader>
        <CardContent className="pt-0">
          <Tabs defaultValue="posts" className="w-full">
            <TabsList>
              <TabsTrigger value="posts">Posts</TabsTrigger>
              <TabsTrigger value="comments">Comments</TabsTrigger>
              <TabsTrigger value="articles">Articles</TabsTrigger>
            </TabsList>
            <TabsContent value="posts" className="space-y-6 mt-6">
              {posts.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-gray-600">No posts yet. Share your training insights!</p>
                </div>
              ) : (
                posts.map((post) => (
                  <PostCard
                    key={post.id}
                    post={post}
                    onLike={handleLikePost}
                  />
                ))
              )}
            </TabsContent>
            <TabsContent value="comments" className="mt-6">
              <div className="text-center py-8">
                <p className="text-gray-600">No comments to display.</p>
              </div>
            </TabsContent>
            <TabsContent value="articles" className="mt-6">
              <div className="text-center py-8">
                <p className="text-gray-600">No articles published yet.</p>
              </div>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* SimplifyMyTraining Profile Modal */}
      {showTrainerProfile && (
        <SimplifyMyTrainingProfile 
          onClose={() => setShowTrainerProfile(false)}
        />
      )}
    </div>
  );
};

export default Profile;